// /Users/donovan68/dva-frontend-vite/src/components/profile/SectionROI.jsx - FIXED VERSION
import React, { useState } from 'react';
import { 
  BarChart3, TrendingUp, DollarSign, AlertCircle, FileText, Calculator, 
  Download, ExternalLink
} from 'lucide-react';
import KeyMetricsGrid from './roi/KeyMetricsGrid';  // lowercase 'roi'
import Waterfall from './roi/Waterfall';  // lowercase 'roi'
import ScenarioGuidance from './roi/ScenarioGuidance';  // lowercase 'roi'
import ScenarioComparisonDashboard from './roi/ScenarioComparisonDashboard';  // lowercase 'roi'
import CalculationBreakdown from './roi/CalculationBreakdown';  // lowercase 'roi'
import FinancialVsStrategic from './roi/FinancialVsStrategic';  // lowercase 'roi'
import AIInsightPanel from './roi/AIInsightPanel';  // Iteration 6: AI Insight Panel
import { useRoiData } from '../../hooks/useRoiData';


export default function SectionROI({ clientId, useCaseId, profileData }) {
  const [activeView, setActiveView] = useState('waterfall');
  
  // Use updated hook with comparison support
  const {
    loading,
    error,
    activeScenario,
    setActiveScenario,
    comparison,
    model,
    recommendation
  } = useRoiData(clientId, useCaseId);
  
  // Format helpers
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0';
    const numValue = Number(value);
    const absValue = Math.abs(numValue);
    
    if (absValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(1)}M`;
    }
    if (absValue >= 1000) {
      return `$${(numValue / 1000).toFixed(0)}K`;
    }
    return `$${numValue.toFixed(0)}`;
  };
  
  const formatPercent = (value) => {
    if (!value || isNaN(value)) return '0%';
    return `${Math.round(value)}%`;
  };
  
  // Get base model for calculation breakdown
  const baseModel = comparison?.find(c => c.scenario === 'base')?.model;
  const activeComparison = comparison?.find(c => c.scenario === activeScenario);
  
  // Clean assumptions data for display
  const enrichedAssumptions = model?.tables?.assumptions?.length > 0 ? 
    model.tables.assumptions.map(a => ({
      label: a.label,
      value: typeof a.value === 'number' ? 
        (a.unit === 'USD' ? formatCurrency(a.value) : `${a.value} ${a.unit || ''}`) : 
        a.value,
      source: a.source || a.source_type,
      confidence: a.confidence_score > 0.8 ? 'High' : 
                  a.confidence_score > 0.6 ? 'Medium' : 'Low',
      confidence_score: a.confidence_score || 0.75
    })) : [];
  
  // Clean sources data
  const enrichedSources = model?.tables?.sources?.length > 0 ? 
    model.tables.sources.map(s => ({
      name: s.source_name || s.name,
      type: s.source_type || s.type || 'public',
      link: s.link
    })) : [];
  
  // Export function for assumptions
  const exportAssumptions = () => {
    const csvContent = [
      ['Assumption', 'Value', 'Source', 'Confidence'],
      ...enrichedAssumptions.map(a => [a.label, a.value, a.source, a.confidence])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roi-assumptions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  // Loading state
  if (loading) {
    return (
      <section className="space-y-4">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-100 rounded-lg mb-6"></div>
          <div className="h-64 bg-gray-100 rounded-lg"></div>
        </div>
      </section>
    );
  }
  
  // Error state
  if (error) {
    return (
      <section className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error loading ROI data</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </section>
    );
  }
  
  // Extract strategic data from profileData
  const strategicData = {
    score: Math.round(profileData?.total_score || profileData?.totalScore || 20),
    rank: profileData?.rank || 75,
    label: profileData?.priority_label || 'Low Priority'
  };
  
  return (
    <section className="space-y-10">
      {/* Scenario Guidance */}
      <ScenarioGuidance />
      
      {/* Scenario Comparison Dashboard */}
      {comparison && (
        <ScenarioComparisonDashboard
          comparison={comparison}
          activeScenario={activeScenario}
          onSelect={setActiveScenario}
        />
      )}
      
      {/* Calculation Breakdown */}
      {baseModel && activeComparison && (
        <CalculationBreakdown
          baseBenefitsY1={baseModel.metrics.benefitY1}
          multiplier={activeComparison.multiplier}
          adjustedBenefitsY1={model.metrics.benefitY1}
          investmentY1={model.metrics.investmentY1}
        />
      )}
      
      {/* Key Metrics Grid */}
      {model && (
        <KeyMetricsGrid metrics={{ ...model.metrics, scenario: activeScenario }} />
      )}
      
      {/* View Toggle Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          {['waterfall', 'breakdown', 'sensitivity'].map((view) => (
            <button
              key={view}
              onClick={() => setActiveView(view)}
              className={`pb-2 px-1 capitalize font-medium transition-colors border-b-2 ${
                activeView === view 
                  ? 'text-blue-600 border-blue-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {view} Analysis
            </button>
          ))}
        </div>
      </div>
      
      {/* WATERFALL VIEW */}
      {activeView === 'waterfall' && model && (
        <Waterfall 
          drivers={model.drivers} 
          investment={model.metrics.investmentY1}
          scenario={activeScenario}
        />
      )}
      
      {/* BREAKDOWN VIEW */}
      {activeView === 'breakdown' && model && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Revenue Drivers
              {activeScenario !== 'base' && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  {activeScenario === 'conservative' ? '0.7×' : '1.3×'}
                </span>
              )}
            </h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                {model.drivers?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{item.label}</span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(item.value)}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-2">
                <div className="flex items-center justify-between font-semibold">
                  <span>Total Benefits</span>
                  <span className="text-green-600">
                    {formatCurrency(model.metrics.benefitY1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Investment Requirements
            </h3>
            
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Initial Setup</span>
                  <span className="font-semibold">
                    {formatCurrency(model.metrics.investmentY1 * 0.33)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Technology & Tools</span>
                  <span className="font-semibold">
                    {formatCurrency(model.metrics.investmentY1 * 0.33)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Training & Support</span>
                  <span className="font-semibold">
                    {formatCurrency(model.metrics.investmentY1 * 0.22)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Contingency</span>
                  <span className="font-semibold">
                    {formatCurrency(model.metrics.investmentY1 * 0.12)}
                  </span>
                </div>
                <div className="border-t pt-2 flex items-center justify-between font-semibold">
                  <span>Total Investment</span>
                  <span>{formatCurrency(model.metrics.investmentY1)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* SENSITIVITY VIEW - Iteration 6: Enhanced ranking hierarchy */}
      {activeView === 'sensitivity' && model && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-base">
            <Calculator className="w-5 h-5 text-[#18365E]" />
            Sensitivity Analysis
          </h3>
          <p className="text-gray-600 mb-6 text-sm">Impact of ±10% change in key variables on ROI outcome</p>
          
          <div className="space-y-3">
            {(() => {
              // Sort by impact and assign ranks
              const sortedSensitivity = [...(model.sensitivity || [])].sort((a, b) => (b.impactPercent || 0) - (a.impactPercent || 0));
              
              return model.sensitivity?.map((item, idx) => {
                const rank = sortedSensitivity.findIndex(s => s.label === item.label) + 1;
                const isPrimary = rank === 1;
                const isSecondary = rank === 2;
                const isTertiary = rank >= 3 && rank <= 5;
                const isLowRank = rank > 5;
                
                // Calculate bar width for visual hierarchy
                const barWidth = isPrimary ? 'w-24' : isSecondary ? 'w-20' : isTertiary ? 'w-16' : 'w-12';
                const barHeight = isPrimary ? 'h-3' : isSecondary ? 'h-2.5' : 'h-2';
                const barColor = isPrimary ? 'bg-[#18365E]' : isSecondary ? 'bg-[#18365E]/80' : isTertiary ? 'bg-[#18365E]/60' : 'bg-gray-300';
                
                return (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 border transition-all group relative ${
                      (isPrimary || isSecondary) ? 'border-gray-300 bg-gray-50/50' : 'border-gray-200'
                    } ${isLowRank ? 'opacity-75' : ''}`}
                  >
                    {/* Impact rank badge - Iteration 6: Enhanced sizing */}
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`flex-shrink-0 rounded-full flex items-center justify-center font-bold ${
                        isPrimary ? 'w-10 h-10 bg-[#18365E] text-white text-sm shadow-md' :
                        isSecondary ? 'w-9 h-9 bg-[#18365E]/90 text-white text-xs shadow-sm' :
                        isTertiary ? 'w-8 h-8 bg-gray-400 text-white text-xs' :
                        'w-7 h-7 bg-gray-300 text-gray-600 text-[10px]'
                      }`}>
                        #{rank}
                      </div>
                      
                      {/* Visual impact bar - Iteration 6: Added for hierarchy */}
                      <div className={`${barWidth} ${barHeight} ${barColor} rounded-full mr-3`}></div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm ${isPrimary ? 'font-bold text-[#18365E]' : isSecondary ? 'font-semibold text-gray-900' : 'font-medium'} ${isLowRank ? 'text-gray-500' : 'text-gray-900'}`}>
                            {item.label}
                          </span>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            item.impactPercent > 30 ? 'bg-red-100 text-red-800' : 
                            item.impactPercent > 15 ? 'bg-amber-100 text-amber-800' : 
                            'bg-emerald-100 text-emerald-800'
                          }`}>
                            {item.impactPercent > 30 ? 'High' : 
                             item.impactPercent > 15 ? 'Medium' : 'Low'} impact
                          </span>
                        </div>
                        <div className="flex items-center gap-6 text-xs text-gray-500">
                          <span>Range: ±10%</span>
                          <span>Effect: {item.effect}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Impact explanation tooltip on hover */}
                    <div className="absolute left-0 right-0 top-full mt-2 hidden group-hover:block z-10 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl">
                      <div className="font-semibold mb-1">{item.label}</div>
                      <div className="text-gray-300">
                        {item.explanation || `A ±10% change in this variable results in ${item.impactPercent}% impact on overall ROI. This ${item.impactPercent > 30 ? 'high' : item.impactPercent > 15 ? 'moderate' : 'low'}-impact driver ${item.impactPercent > 30 ? 'significantly influences' : 'moderately affects'} the financial model.`}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
      
      {/* ASSUMPTIONS TABLE - Iteration 4 Enhanced */}
      {enrichedAssumptions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
              <AlertCircle className="w-5 h-5 text-[#18365E]" />
              Key Assumptions & Model Inputs
            </h3>
            <button 
              onClick={exportAssumptions}
              className="px-4 py-2 text-sm bg-[#18365E] hover:bg-[#142b4b] text-white rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-700 text-xs uppercase tracking-wide">Assumption</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700 text-xs uppercase tracking-wide">Value</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700 text-xs uppercase tracking-wide">Source</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700 text-xs uppercase tracking-wide">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {enrichedAssumptions.slice(0, 10).map((assumption, idx) => {
                  // Determine category icon based on assumption label
                  const getCategoryIcon = (label) => {
                    const lowerLabel = label.toLowerCase();
                    if (lowerLabel.includes('revenue') || lowerLabel.includes('cost') || lowerLabel.includes('financial') || lowerLabel.includes('dollar') || lowerLabel.includes('$')) {
                      return '💵';
                    } else if (lowerLabel.includes('operational') || lowerLabel.includes('process') || lowerLabel.includes('efficiency')) {
                      return '⚙️';
                    } else if (lowerLabel.includes('population') || lowerLabel.includes('volume') || lowerLabel.includes('patient') || lowerLabel.includes('user')) {
                      return '👥';
                    } else if (lowerLabel.includes('program') || lowerLabel.includes('grant') || lowerLabel.includes('initiative')) {
                      return '🎯';
                    }
                    return '📊';
                  };

                  // Determine source icon
                  const getSourceIcon = (source) => {
                    const lowerSource = source?.toLowerCase() || '';
                    if (lowerSource.includes('benchmark') || lowerSource.includes('external')) {
                      return '📈';
                    } else if (lowerSource.includes('historical') || lowerSource.includes('past')) {
                      return '📅';
                    } else if (lowerSource.includes('estimate') || lowerSource.includes('projection')) {
                      return '🔮';
                    }
                    return '📄';
                  };

                  return (
                    <tr 
                      key={idx} 
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        idx % 2 === 0 ? 'bg-gray-50/30' : 'bg-white'
                      }`}
                    >
                      <td className="py-5 px-4">
                        <div className="flex items-center gap-3">
                          <span className="text-lg flex-shrink-0">{getCategoryIcon(assumption.label)}</span>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{assumption.label}</div>
                            {/* Placeholder for tooltip - could add Info icon here */}
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-4">
                        <div className="font-semibold text-gray-900 text-sm">{assumption.value}</div>
                      </td>
                      <td className="py-5 px-4">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <span className="text-base">{getSourceIcon(assumption.source)}</span>
                          <span>{assumption.source || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="py-5 px-4">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                          assumption.confidence === 'High' ? 'bg-emerald-100 text-emerald-800' :
                          assumption.confidence === 'Medium' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {assumption.confidence}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Financial vs Strategic Analysis */}
      <FinancialVsStrategic
        financial={{
          roiPct: model?.metrics?.roiPct,
          paybackMonths: model?.metrics?.paybackMonths,
          recommendation: recommendation
        }}
        strategic={strategicData}
      />
      
      {/* Iteration 6: AI Insight Panel */}
      {recommendation && (
        <AIInsightPanel 
          recommendation={recommendation}
          insight={recommendation?.message || recommendation?.rationale}
        />
      )}
      
      {/* DATA SOURCES & REFERENCES */}
      {enrichedSources.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Data Sources & References
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enrichedSources.map((source, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      {source.name}
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                        source.type === 'public' ? 'bg-blue-100 text-blue-800' :
                        source.type === 'vendor' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {source.type}
                      </span>
                    </div>
                  </div>
                  {source.link && source.link.startsWith('http') && (
                    <a 
                      href={source.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                    >
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}